import React from 'react';
import { useLang } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { socials } from '../data/mock';
import { MapPin, Clock, Instagram } from 'lucide-react';

const Contact = () => {
  const { t, lang } = useLang();
  const { content } = useContent();

  const hours = content?.hours || [];

  const today = (new Date().getDay() + 6) % 7; // 0 = Mon

  return (
    <section id="contact" className="section-pad bg-white">
      <div className="container-tight">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <span className="eyebrow">{t.contact.eyebrow}</span>
          <h2 className="h-display mt-6" style={{ fontSize: 'clamp(40px, 5vw, 64px)' }}>
            {t.contact.title}
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={16} className="text-[#957340]" />
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#8A8275]">{t.contact.addressLabel}</span>
              </div>
              <p className="serif text-[22px] leading-[1.4] text-[#1A1A1A]">{t.contact.address}</p>
              <a
                href="https://www.google.com/maps/place/Devil+Tattooz/data=!4m2!3m1!1s0x0:0x1b2b9bd813bfa608"
                target="_blank" rel="noreferrer"
                className="link-underline mt-4 inline-block"
              >
                {t.contact.directions}
              </a>
            </div>

            <div className="border-t border-[#E8E2D5] pt-8">
              <div className="flex items-center gap-3 mb-5">
                <Clock size={16} className="text-[#957340]" />
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#8A8275]">{t.contact.hoursLabel}</span>
              </div>
              <ul className="space-y-2">
                {Array.from({ length: 7 }, (_, i) => {
                  const h = hours.find((x) => x.day === i) || { closed: true };
                  const isToday = today === i;
                  let hoursLabel;
                  if (h.closed) {
                    hoursLabel = t.contact.closed;
                  } else if (lang === 'fr') {
                    hoursLabel = h.openFr;
                  } else {
                    hoursLabel = h.openEn;
                  }
                  return (
                    <li key={`hr-${i}`} className={`flex items-center justify-between py-2 border-b border-[#E8E2D5]/60 ${isToday ? 'font-medium' : ''}`}>
                      <span className="text-[14px]" style={{ color: isToday ? '#957340' : '#1A1A1A' }}>
                        {t.contact.days[i]}
                        {isToday && <span className="ml-2 text-[9px] tracking-[0.3em] uppercase text-[#957340]">— {lang === 'fr' ? 'aujourd’hui' : 'today'}</span>}
                      </span>
                      <span className="text-[13px] text-[#3A3A3A] tracking-wide">{hoursLabel}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border-t border-[#E8E2D5] pt-8">
              <div className="flex items-center gap-3 mb-5">
                <Instagram size={16} className="text-[#957340]" />
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#8A8275]">{t.contact.followLabel}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href={socials.instagram} target="_blank" rel="noreferrer" className="btn-ghost" style={{ padding: '10px 18px' }}>@devil_perceur</a>
                <a href={socials.tiktok} target="_blank" rel="noreferrer" className="btn-ghost" style={{ padding: '10px 18px' }}>TikTok</a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A2018] text-white border border-[#957340]/30 p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#957340]/10 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <Instagram size={18} className="text-[#D4B987]" />
                  <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4B987]">
                    {lang === 'fr' ? 'Réservation via Instagram' : 'Booking via Instagram'}
                  </span>
                </div>
                <h3 className="h-display text-[34px] md:text-[44px] leading-tight text-white">
                  {lang === 'fr' ? 'Réservez en un DM.' : 'Book in a single DM.'}
                </h3>
                <p className="text-[14px] text-white/70 mt-4 mb-8 leading-[1.8] max-w-lg">
                  {lang === 'fr'
                    ? 'Toutes nos réservations passent désormais par Instagram. Envoyez-nous un message privé avec votre demande, nous vous répondons rapidement.'
                    : 'All our bookings now go through Instagram. Send us a DM with your request and we\u2019ll reply quickly.'}
                </p>
                <a
                  href={socials.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4B987] text-[#0E0E0E] text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors"
                >
                  <Instagram size={16} /> @devil_perceur
                </a>
                <div className="mt-8 pt-6 border-t border-white/10 text-[12px] text-white/50">
                  {lang === 'fr'
                    ? 'Vous pouvez aussi passer sans rendez-vous durant nos horaires d\u2019ouverture.'
                    : 'You can also walk in during our opening hours.'}
                </div>
              </div>
            </div>

            <div className="mt-6 aspect-[16/9] border border-[#E8E2D5] overflow-hidden">
              <iframe
                title="map"
                src="https://www.google.com/maps?q=17+Rue+Louis+Lenoir,+94350+Villiers-sur-Marne,+France&output=embed"
                className="w-full h-full"
                style={{ filter: 'grayscale(0.6) contrast(0.95)' }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
